import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { Logo } from "@/components/BrandGlyph";
import { useCart } from "@/context/CartContext";

const NAV = [
  { to: "/product", label: "Shop" },
  { to: "/#specs", label: "Specifications" },
  { to: "/#faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
  { to: "/cart", label: "Basket", mobileOnly: true },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { itemCount, openDrawer } = useCart();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menu on route or hash change
  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.hash]);

  // Body scroll lock + ESC dismiss while the mobile menu is open
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
      }
    }
  }, [location]);

  const desktopNav = NAV.filter((n) => !n.mobileOnly);
  const mobileNav = NAV;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 bg-white transition-shadow duration-300 ${
        scrolled ? "shadow-soft border-b border-silver/40" : "border-b border-transparent"
      }`}
    >
      <div className="container-pc flex h-[68px] items-center justify-between">
        <Link to="/" aria-label="PeptiChill home" data-testid="header-logo-link">
          <Logo />
        </Link>

        <nav
          className="hidden md:flex items-center gap-1"
          data-nav-mode="desktop"
          data-testid="header-nav"
        >
          {desktopNav.map((item) => (
            <NavItem key={item.to} to={item.to} label={item.label} />
          ))}
        </nav>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={openDrawer}
            aria-label={`Open basket — ${itemCount} item${itemCount === 1 ? "" : "s"}`}
            data-testid="header-cart-button"
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-full text-navy transition-colors hover:bg-ice active:bg-ice/80"
          >
            <ShoppingBag size={20} strokeWidth={1.8} />
            {itemCount > 0 && (
              <span
                data-testid="header-cart-badge"
                className="absolute top-1 right-1 min-w-[18px] h-[18px] inline-flex items-center justify-center rounded-full bg-cyan text-navy-deep text-[10px] font-bold tabular-nums px-1 leading-none"
              >
                {itemCount > 99 ? "99+" : itemCount}
              </span>
            )}
          </button>

          <button
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="header-mobile-panel"
            data-testid="header-mobile-toggle"
            data-nav-mode="mobile"
            className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-full text-navy hover:bg-ice active:bg-ice/80"
            onClick={() => setMobileOpen((s) => !s)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu — semi-transparent backdrop + dropdown panel.
       *  Tapping the visible backdrop strip dismisses the menu (body scroll
       *  is locked while open). */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 top-[calc(2rem+68px)] bg-navy/30 backdrop-blur-sm z-30"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
            data-testid="header-mobile-backdrop"
          />
          <div
            id="header-mobile-panel"
            className="md:hidden border-t border-silver/40 bg-white animate-fade-up relative z-40"
            data-testid="header-mobile-panel"
          >
            <nav className="container-pc flex flex-col py-2">
              {mobileNav.map((item) => (
                <NavItem key={item.to} to={item.to} label={item.label} mobile />
              ))}
            </nav>
          </div>
        </>
      )}
    </header>
  );
}

function NavItem({ to, label, mobile = false }) {
  const isAnchor = to.startsWith("/#");
  // Mobile rows are bumped to min-h-12 (48px) for thumb-friendly taps.
  const mobileCls = "min-h-[48px] flex items-center py-3 text-base";
  const desktopCls = "px-4 py-2 text-sm";
  const className = ({ isActive }) =>
    `${mobile ? mobileCls : desktopCls} font-medium rounded-full transition-colors ${
      isActive ? "text-navy" : "text-ink hover:text-navy"
    }`;
  if (isAnchor) {
    return (
      <a
        href={to.replace("/", "")}
        className={`${mobile ? mobileCls : desktopCls} font-medium rounded-full text-ink hover:text-navy transition-colors`}
        data-testid={`nav-${label.toLowerCase()}-link`}
      >
        {label}
      </a>
    );
  }
  return (
    <NavLink
      to={to}
      className={className}
      data-testid={`nav-${label.toLowerCase()}-link`}
      end={to === "/"}
    >
      {label}
    </NavLink>
  );
}
